import { asInstanceOf, isDefined, Notifier, Option, StringMapping, Terminable, ValueMapping } from "@opendaw/lib-std";
import { WerkstattParameterBox } from "@opendaw/studio-boxes";
const LABEL_LINE = /^\/\/ @label .+$/m;
const PARAM_LINE = /^\/\/ @param .+$/gm;
const SAMPLE_LINE = /^\/\/ @sample .+$/gm;
const GROUP_LINE = /^\/\/ @group .+$/gm;
const DIRECTIVE_LINE = /^\/\/ @(group|param|sample) .+$/gm;
const DECLARATION_LINE = /^\/\/ @(?:param|sample) \S+/gm;
const FLOAT_TOLERANCE = 1e-6;
const VALID_MAPPINGS = ["linear", "exp", "int", "bool"];
const BoolStringMapping = new class {
    x(y) {
        return { value: y >= 0.5 ? "On" : "Off", unit: "" };
    }
    y(x) {
        const lower = x.trim().toLowerCase();
        return { type: "explicit", value: (lower === "on" || lower === "true" || lower === "yes") ? 1 : 0 };
    }
};
const parseSingleParam = (line) => {
    const tokens = line.replace(/^\/\/ @param\s+/, "").replace(/\s+\/\/.*$/, "").trim().split(/\s+/);
    if (tokens.length === 0) {
        throw new Error(`Malformed @param: '${line}'`);
    }
    const label = tokens[0];
    if (tokens.length === 1) {
        return { label, defaultValue: 0, min: 0, max: 1, mapping: "unipolar", unit: "" };
    }
    const second = tokens[1];
    if (second === "true" || second === "false") {
        return { label, defaultValue: second === "true" ? 1 : 0, min: 0, max: 1, mapping: "bool", unit: "" };
    }
    if (second === "bool") {
        return { label, defaultValue: 0, min: 0, max: 1, mapping: "bool", unit: "" };
    }
    const defaultValue = parseFloat(second);
    if (isNaN(defaultValue)) {
        throw new Error(`Malformed @param: '${line}' — '${second}' is not a valid number`);
    }
    if (tokens.length === 2) {
        return { label, defaultValue, min: 0, max: 1, mapping: "unipolar", unit: "" };
    }
    if (tokens.length === 3 && tokens[2] === "bool") {
        return { label, defaultValue: defaultValue >= 0.5 ? 1 : 0, min: 0, max: 1, mapping: "bool", unit: "" };
    }
    if (tokens.length === 4) {
        const min = parseFloat(tokens[2]);
        const max = parseFloat(tokens[3]);
        if (isNaN(min) || isNaN(max)) {
            throw new Error(`Malformed @param: '${line}' — min/max must be numbers`);
        }
        if (max - min < FLOAT_TOLERANCE) {
            throw new Error(`Malformed @param: '${line}' — min (${min}) must be less than max (${max})`);
        }
        if (defaultValue < min - FLOAT_TOLERANCE || defaultValue > max + FLOAT_TOLERANCE) {
            throw new Error(`Malformed @param: '${line}' — default (${defaultValue}) must be within [${min}, ${max}]`);
        }
        return { label, defaultValue, min, max, mapping: "linear", unit: "" };
    }
    if (tokens.length < 5) {
        throw new Error(`Malformed @param: '${line}' — expected: // @param <name> <default> <min> <max> [type] [unit]`);
    }
    const min = parseFloat(tokens[2]);
    const max = parseFloat(tokens[3]);
    const mapping = tokens[4];
    const unit = tokens.length >= 6 ? tokens[5] : "";
    if (isNaN(min) || isNaN(max)) {
        throw new Error(`Malformed @param: '${line}' — min/max must be numbers`);
    }
    if (!VALID_MAPPINGS.includes(mapping)) {
        throw new Error(`Malformed @param: '${line}' — unknown mapping '${mapping}' (expected: linear, exp, int, bool)`);
    }
    if (mapping !== "bool" && max - min < FLOAT_TOLERANCE) {
        throw new Error(`Malformed @param: '${line}' — min (${min}) must be less than max (${max})`);
    }
    if (defaultValue < min - FLOAT_TOLERANCE || defaultValue > max + FLOAT_TOLERANCE) {
        throw new Error(`Malformed @param: '${line}' — default (${defaultValue}) must be within [${min}, ${max}]`);
    }
    return { label, defaultValue, min, max, mapping, unit };
};
const declarationEquals = (a, b) => a.mapping === b.mapping && a.min === b.min && a.max === b.max && a.unit === b.unit;
const declarationFullEquals = (a, b) => declarationEquals(a, b) && Math.abs(a.defaultValue - b.defaultValue) < 1e-6;
export var ScriptDeclaration;
(function (ScriptDeclaration) {
    ScriptDeclaration.isEqual = declarationFullEquals;
    ScriptDeclaration.parseLabel = (code) => {
        const match = LABEL_LINE.exec(code);
        if (match === null) {
            return Option.None;
        }
        const label = match[0].replace(/^\/\/ @label\s+/, "").trim();
        if (label.length === 0) {
            throw new Error("Malformed @label: expected: // @label <name>");
        }
        return Option.wrap(label);
    };
    ScriptDeclaration.parseParams = (code) => {
        const params = [];
        let match;
        PARAM_LINE.lastIndex = 0;
        while ((match = PARAM_LINE.exec(code)) !== null) {
            params.push(parseSingleParam(match[0]));
        }
        return params;
    };
    ScriptDeclaration.parseSamples = (code) => {
        const samples = [];
        let match;
        SAMPLE_LINE.lastIndex = 0;
        while ((match = SAMPLE_LINE.exec(code)) !== null) {
            const tokens = match[0].replace(/^\/\/ @sample\s+/, "").replace(/\s+\/\/.*$/, "").trim().split(/\s+/);
            if (tokens.length === 0 || tokens[0].length === 0) {
                throw new Error(`Malformed @sample: '${match[0]}' — expected: // @sample <name>`);
            }
            if (tokens.length > 1) {
                throw new Error(`Malformed @sample: '${match[0]}' — expected: // @sample <name>`);
            }
            samples.push({ label: tokens[0] });
        }
        return samples;
    };
    ScriptDeclaration.parseDeclarationOrder = (code) => {
        const order = new Map();
        let match;
        DECLARATION_LINE.lastIndex = 0;
        let index = 0;
        while ((match = DECLARATION_LINE.exec(code)) !== null) {
            const label = match[0].replace(/^\/\/ @(?:param|sample)\s+/, "").split(/\s+/)[0];
            if (!order.has(label)) {
                order.set(label, index++);
            }
        }
        return order;
    };
    ScriptDeclaration.parseGroups = (code) => {
        const sections = [];
        let currentGroup = null;
        let currentItems = [];
        DIRECTIVE_LINE.lastIndex = 0;
        let match;
        while ((match = DIRECTIVE_LINE.exec(code)) !== null) {
            const line = match[0];
            const type = match[1];
            if (type === "group") {
                if (currentItems.length > 0 || currentGroup !== null) {
                    sections.push({ group: currentGroup, items: currentItems });
                }
                const tokens = line.replace(/^\/\/ @group\s+/, "").trim().split(/\s+/);
                currentGroup = { label: tokens[0], color: tokens[1] ?? "dark" };
                currentItems = [];
            }
            else if (type === "param") {
                currentItems.push({ type: "param", declaration: parseSingleParam(line) });
            }
            else if (type === "sample") {
                const tokens = line.replace(/^\/\/ @sample\s+/, "").replace(/\s+\/\/.*$/, "").trim().split(/\s+/);
                if (tokens.length === 0 || tokens[0].length === 0) {
                    throw new Error(`Malformed @sample: '${line}' — expected: // @sample <name>`);
                }
                currentItems.push({ type: "sample", declaration: { label: tokens[0] } });
            }
        }
        if (currentItems.length > 0 || currentGroup !== null) {
            sections.push({ group: currentGroup, items: currentItems });
        }
        return sections;
    };
    ScriptDeclaration.resolveValueMapping = (declaration) => {
        switch (declaration.mapping) {
            case "unipolar":
                return ValueMapping.unipolar();
            case "linear":
                return ValueMapping.linear(declaration.min, declaration.max);
            case "exp":
                return ValueMapping.exponential(declaration.min, declaration.max);
            case "int":
                return ValueMapping.linearInteger(declaration.min, declaration.max);
            case "bool":
                return ValueMapping.linearInteger(0, 1);
        }
    };
    ScriptDeclaration.resolveStringMapping = (declaration) => {
        switch (declaration.mapping) {
            case "unipolar":
                return StringMapping.percent();
            case "linear":
                return StringMapping.numeric({ unit: declaration.unit, fractionDigits: 2 });
            case "exp":
                return StringMapping.numeric({ unit: declaration.unit, fractionDigits: 2 });
            case "int":
                return StringMapping.numeric({ unit: declaration.unit, fractionDigits: 0 });
            case "bool":
                return BoolStringMapping;
        }
    };
    ScriptDeclaration.resolveParamMappings = (declaration) => ({
        valueMapping: ScriptDeclaration.resolveValueMapping(declaration),
        stringMapping: ScriptDeclaration.resolveStringMapping(declaration)
    });
    ScriptDeclaration.subscribeScriptParams = (parametric, codeField, parametersField) => {
        const cachedDeclarations = new Map();
        const codeChangedNotifier = new Notifier();
        const terminable = Terminable.many(parametersField.pointerHub.catchupAndSubscribe({
            onAdded: (({ box: parameterBox }) => {
                const paramBox = asInstanceOf(parameterBox, WerkstattParameterBox);
                const label = paramBox.label.getValue();
                const declarations = ScriptDeclaration.parseParams(codeField.getValue());
                const declaration = declarations.find(decl => decl.label === label);
                const { valueMapping, stringMapping } = isDefined(declaration)
                    ? ScriptDeclaration.resolveParamMappings(declaration)
                    : {
                        valueMapping: ValueMapping.unipolar(),
                        stringMapping: StringMapping.percent({ fractionDigits: 1 })
                    };
                parametric.createParameter(paramBox.value, valueMapping, stringMapping, label, undefined, paramBox.defaultValue.getValue());
                if (isDefined(declaration)) {
                    cachedDeclarations.set(label, declaration);
                }
            }),
            onRemoved: (({ box }) => {
                const paramBox = asInstanceOf(box, WerkstattParameterBox);
                cachedDeclarations.delete(paramBox.label.getValue());
                parametric.removeParameter(paramBox.value.address);
            })
        }), codeField.subscribe(() => {
            const declarations = ScriptDeclaration.parseParams(codeField.getValue());
            for (const adapter of parametric.parameters()) {
                const newDeclaration = declarations.find(decl => decl.label === adapter.name);
                if (!isDefined(newDeclaration)) {
                    continue;
                }
                const oldDeclaration = cachedDeclarations.get(adapter.name);
                if (isDefined(oldDeclaration) && declarationEquals(oldDeclaration, newDeclaration)) {
                    continue;
                }
                const { valueMapping, stringMapping } = ScriptDeclaration.resolveParamMappings(newDeclaration);
                adapter.updateMappings(valueMapping, stringMapping);
                cachedDeclarations.set(adapter.name, newDeclaration);
            }
            codeChangedNotifier.notify();
        }), codeChangedNotifier);
        return { terminable, codeChanged: codeChangedNotifier };
    };
})(ScriptDeclaration || (ScriptDeclaration = {}));
